import axios, { CancelTokenSource } from 'axios';
import DeleteActionButton from 'components/DeleteActionButton';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import { DropdownOption } from 'components/final-form/dropdownUtils';
import ErrorMessage from 'components/final-form/ErrorMessage';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import TariffDropdownRow from 'components/settings/location/TariffDropdownRow';
import { FormApi } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid, GridColumn, GridRow, Table } from 'semantic-ui-react';
import { addLocation, editLocation } from 'services/locationServices';
import styled from 'styled-components';
import { UpsertLocationDto } from 'ts-types/api.types';
import { IndexSignature } from 'utils/praxTypes';
import { required } from 'utils/validatorUtils';

const ScrollableTableContainer = styled.div`
  height: 260px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34, 36, 38, .15);
  box-shadow: none;
  border-radius: .28571429rem;
  margin-bottom: 1em;

  & .ui.table {
    border: none;
  }

  & .ui.table thead tr:first-child > th {
    position: sticky !important;
    top: 0;
    z-index: 2;
    background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
    border-bottom: 1px solid rgba(34, 36, 38, .15)
  }

  & .deleted-product {
    color: rgb(198, 198, 203);
  }
`;

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  locationId?: number,
  location?: UpsertLocationDto,
  tariffOptions: DropdownOption[]
  tariffMap: IndexSignature
}

interface State {
  cancelTokenSource: CancelTokenSource,
}

class UpsertLocationForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }
  }

  initialValues = (): Partial<UpsertLocationDto> => {
    return {
      active: false,
      locationTariffs: []
    }
  };

  handleSubmit = async (values: Partial<UpsertLocationDto>) => {

    let saveValues: Partial<UpsertLocationDto> = {...values};

    let errorMessage: string | Array<string> | undefined = undefined;

    const handleError = () => {
      errorMessage = getMessage("error.general");
    };

    const {locationId, getMessage, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    const locationTariffs: number[] | undefined = saveValues.locationTariffs;
    const defaultTariff: number | undefined = values.defaultTariff;

    if (!locationTariffs || !(locationTariffs.length)) {
      return {
        errorMessage: getMessage("location.error.addTariff")
      }
    }

    if (!defaultTariff) {
      return {
        errorMessage: getMessage("location.error.addDefaultTariff")
      }
    }


    if (locationId) {
      await editLocation(locationId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e) => {
            handleError();
          })
    } else {
      await addLocation(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((e) => {
            handleError();
          })
    }

    if (errorMessage) {
      return errorMessage;
    }
  };

  deleteLocationTariff = (tariffId: number, form: FormApi) => {
    let locationTariffs = [...form.getState().values.locationTariffs];
    locationTariffs = locationTariffs.filter(lt => lt !== tariffId);

    const defaultTariff = form.getState().values.defaultTariff;
    if (defaultTariff === tariffId) {
      form.change("defaultTariff", undefined);
    }
    form.change("locationTariffs", locationTariffs);
  };


  setDefaultTariff = (value: number, form: FormApi) => {
    form.change("defaultTariff", value);
  }

  unsetDefaultTariff = (form: FormApi) => {
    form.change("defaultTariff", undefined);
  }

  render(): React.ReactNode {

    const {location = this.initialValues()} = this.props;

    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={location}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              <Field
                  name="errorMessage"
                  component={ErrorMessage}
                  onDismiss={() => {
                    form.reset(values)
                  }}
              />
              <PraxFormGrid>
                {this.renderFormContent(form, values)}
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("location.button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderFormContent = (form: FormApi, values: any) => {

    const {getMessage, tariffOptions} = this.props;

    const locationTariffs: number[] = values.locationTariffs;
    const filteredTariffOptions = tariffOptions.filter(tr => !locationTariffs.includes(tr.value));

    return (
        <>
          <Grid.Row>
            <Grid.Column width={16} textAlign="right">
              <Field
                  name="active"
                  component={CheckBox}
                  label={`${getMessage("location.isActive")}`}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("location.description")}
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="description"
                  component={Input}
                  validate={required}
                  fluid
              />
            </Grid.Column>
          </Grid.Row>

          <TariffDropdownRow tariffOptions={filteredTariffOptions}
                             addNewTariff={(tariffId: number) => this.addNewTariff(tariffId, form)}
          />

          <GridRow>
            <GridColumn width={16}>
              {this.renderTariffTable(values, form)}
            </GridColumn>
          </GridRow>
        </>
    )
  }

  addNewTariff = (tariffId: number, form: FormApi) => {

    if (!tariffId) {
      return;
    }

    const formValues = form.getState().values;

    const locationTariffs: number[] = [...formValues.locationTariffs, tariffId];

    form.change("locationTariffs", locationTariffs);
  };


  renderTariffTable = (values: any, form: FormApi): React.ReactNode => {
    const {tariffMap, getMessage} = this.props;

    const locationTariffs: number[] = values.locationTariffs;
    const defaultTariff: number | undefined = values.defaultTariff;

    return (
        <ScrollableTableContainer>
          <Table celled size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell width={2}>{getMessage('location.form.item.tariffDesc')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('location.form.item.default')}</Table.HeaderCell>
                <Table.HeaderCell width={2}>{getMessage('location.actions.delete')}</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {locationTariffs.map((tariffId, index) => {
                const tariff = tariffMap[tariffId];
                const checked = defaultTariff === tariffId;
                return (
                    <Table.Row key={index}>
                      <Table.Cell width={2}>
                        {tariff.description}
                      </Table.Cell>
                      <Table.Cell width={2}>
                        <CheckboxSc
                            checked={checked}
                            onChange={() => checked
                                ? this.unsetDefaultTariff(form)
                                : this.setDefaultTariff(tariffId, form)}
                        />
                      </Table.Cell>

                      <Table.Cell width={2}>
                        <DeleteActionButton
                            hoverMessage={getMessage('msgDelete')}
                            onConfirm={() => this.deleteLocationTariff(tariffId, form)}
                        />
                      </Table.Cell>
                    </Table.Row>
                )
              })
              }
            </Table.Body>
          </Table>
        </ScrollableTableContainer>
    );
  }

}

export default withMessages(UpsertLocationForm);
